import { useEffect, useRef, type ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'

import { Toaster } from '@/components/ui/sonner'

interface Props {
  children: ReactNode
}

interface FlashMessages {
  notice?: string
  alert?: string
  error?: string
  _ts?: number // Backend timestamp for deduplication
}

export function Layout({ children }: Props) {
  // Track the last flash timestamp to avoid showing duplicates on reload()
  const lastFlashTsRef = useRef<number | null>(null)

  useEffect(() => {
    // Listen for Inertia page visits to show flash messages
    const removeListener = router.on('success', (event) => {
      const flash = event.detail.page.props.flash as FlashMessages | undefined

      if (!flash?.notice && !flash?.alert && !flash?.error) {
        return
      }

      // Use backend timestamp to detect duplicates
      // Same timestamp = same flash being re-rendered (from reload/navigation)
      // Different timestamp = new flash from a new action
      if (flash._ts && flash._ts === lastFlashTsRef.current) {
        return
      }

      lastFlashTsRef.current = flash._ts ?? null

      // Show flash messages
      if (flash?.notice) {
        toast.success(flash.notice)
      }
      if (flash?.alert) {
        toast.warning(flash.alert)
      }
      if (flash?.error) {
        toast.error(flash.error)
      }
    })

    return () => removeListener()
  }, [])

  return (
    <>
      {children}
      <Toaster position="top-center" />
    </>
  )
}
